import { Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./modules/authentication/context/AuthContext";
import { ProtectedRoute } from "./components/protected-route";
import { AppLayout } from "./components/app-layout";
import ForgotPasswordPage from "./modules/authentication/view/forgot-password";
import LoginPage from "./modules/authentication/view/login-page";
import RegisterPage from "./modules/authentication/view/register-page";
import ResetPasswordPage from "./modules/authentication/view/reset-password";
import ZaloCallbackPage from "./modules/authentication/view/zalo-callback";
import Home from "./modules/home/view";
import LiveKit from "./modules/livekit";
import AccountPage from "./modules/user/view/account";
import { routeConfig } from "./config/menu";

function MeetingRoute() {
  const location = useLocation();
  return <LiveKit key={location.key} />;
}

function HomeWrapper() {
  return <Home />;
}

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen gap-2">
      <div className="animate-spin rounded-full flex items-center justify-center p-1 size-8 bg-primary">
        <svg
          width="20"
          height="13"
          viewBox="0 0 20 13"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1.40741 0C0.630118 0 0 0.692893 0 1.54762V9.05357C0 11.2331 1.6068 13 3.58889 13L11.9259 12.9226C12.7032 12.9226 13.3333 12.2298 13.3333 11.375V3.79169C13.3333 1.61214 11.4265 2.68618e-05 9.44444 2.68618e-05L1.40741 0Z"
            fill="white"
          />
          <path
            d="M15.2358 2.9549C14.7328 3.43976 14.4444 4.13954 14.4444 4.87503V8.01591C14.4444 8.7514 14.7328 9.45118 15.2358 9.93604L18.3653 12.6235C19.0022 13.2374 20 12.7401 20 11.8089V1.22976C20 0.298508 19.0022 -0.198754 18.3654 0.415158L15.2358 2.9549Z"
            fill="white"
          />
        </svg>
      </div>
      <span className="text-gray-600 text-sm">Đang tải...</span>
    </div>
  );
}

export default function Router() {
  const { isAuthenticated } = useAuth();

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <LoginPage />
            )
          }
        />
        <Route path="/zalocallback" element={<ZaloCallbackPage />} />
        <Route
          path="/register"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <RegisterPage />
            )
          }
        />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/meeting/:meetingCode" element={<MeetingRoute />} />
        {/* Home route - accessible for both authenticated and unauthenticated users */}
        <Route path="/home" element={<HomeWrapper />} />
        {/* Protected routes with AppLayout (includes SidebarProvider) */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          {routeConfig
            .filter((route) => route.requiresAuth)
            .map((route) => {
              const RouteComponent = route.component;
              return (
                <Route
                  key={route.path}
                  path={route.path}
                  element={<RouteComponent />}
                />
              );
            })}
        </Route>

        {/* Public routes from routeConfig (without layout) */}
        {routeConfig
          .filter((route) => !route.requiresAuth)
          .map((route) => {
            const RouteComponent = route.component;
            return (
              <Route
                key={route.path}
                path={route.path}
                element={<RouteComponent />}
              />
            );
          })}

        {/* Default route */}
        <Route
          path="*"
          element={
            <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
          }
        />
      </Routes>
    </Suspense>
  );
}
